import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Check, Crown } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  "Unlimited AI Coach conversations",
  "AI-generated monthly insight cards",
  "Live financial context on every reply",
  "All future AI features included",
  "One-time payment — yours for life",
];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Upgrade() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPkg(r.data.premium_lifetime));
    // Pre-load Razorpay script so the modal is instant on click
    loadRazorpayScript();
  }, []);

  // Already premium — bounce to coach
  useEffect(() => {
    if (user?.is_premium) navigate("/coach");
  }, [user, navigate]);

  const checkout = async () => {
    setLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Failed to load payment gateway");

      const { data } = await api.post("/payments/checkout", {
        package_id: "premium_lifetime",
      });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: data.name,
        description: data.description,
        prefill: data.prefill,
        theme: { color: "#111111" },
        modal: {
          ondismiss: () => setLoading(false),
        },
        handler: async (response) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            await refresh();
            navigate(`/billing/success?order_id=${response.razorpay_order_id}`);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
            setLoading(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to start payment");
      setLoading(false);
    }
  };

  const symbol = pkg?.currency === "INR" || pkg?.currency === "inr" ? "₹" : "$";

  return (
    <div className="min-h-[80vh] flex items-center justify-center" data-testid="upgrade-page">
      <div className="w-full max-w-2xl">
        <div className="overline mb-2">UPGRADE / ONE-TIME</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter">
          Unlock the <span className="text-[var(--alert-red)]">AI Coach.</span>
        </h1>
        <p className="font-mono text-sm text-[var(--text-secondary)] mt-3 max-w-lg">
          Get unlimited, context-aware financial coaching powered by Claude Sonnet 4.5.
          Pay once. Use forever. No subscriptions.
        </p>

        <div className="mt-8 bg-[#111111] text-white border border-black p-8 relative overflow-hidden">
          <div className="absolute top-4 right-4 flex flex-col items-end gap-2">
            <Crown size={20} className="text-[var(--alert-red)]" />
            <div className="bg-[var(--alert-red)] text-white font-display font-black text-[10px] px-2 py-1 tracking-widest">
              SPECIAL LAUNCH
            </div>
          </div>

          <div className="overline text-white/60 flex items-center gap-2">
            <Sparkles size={12} /> {pkg?.name || "RED.FLAG PREMIUM"}
          </div>

          <div className="mt-4 flex items-baseline gap-3 flex-wrap">
            <div className="font-mono text-2xl text-white/40 line-through">{symbol}499</div>
            <div className="font-display font-black text-7xl tracking-tighter">
              {symbol}{pkg?.amount ?? 200}
            </div>
            <div className="font-mono text-sm text-white/60">/ once</div>
          </div>
          <div className="font-mono text-xs text-white/60 mt-1">
            {pkg?.currency?.toUpperCase() || "INR"} · lifetime access · <span className="text-[var(--alert-red)] font-bold">SAVE ₹299 · LAUNCH PRICE</span>
          </div>

          <ul className="mt-6 space-y-2 font-mono text-sm">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check size={16} className="text-[var(--success-green)] mt-0.5 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <button
            data-testid="checkout-button"
            onClick={checkout}
            disabled={loading}
            className="mt-8 w-full bg-white text-black py-4 font-display font-bold tracking-wider hover:bg-[var(--alert-red)] hover:text-white transition-colors disabled:opacity-50"
          >
            {loading ? "REDIRECTING…" : `PAY ${symbol}${pkg?.amount ?? 200} →`}
          </button>

          <div className="mt-4 font-mono text-xs text-white/40 text-center">
            Secure checkout via Razorpay. Cancel anytime before payment.
          </div>
        </div>

        <div className="mt-6 font-mono text-sm text-[var(--text-secondary)]">
          Not ready?{" "}
          <Link to="/" className="underline font-bold" data-testid="back-to-dashboard">Back to dashboard</Link>
        </div>
      </div>
    </div>
  );
}
